import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import React from 'react';
import { isPresent, isBlank, pixify } from '@seedlang/utils';

@observer
class InPlaceTextInput extends React.Component {
  @observable temporaryValue = '';

  static defaultProps = {
    style: {},
  };

  @observable expanded = false;

  constructor(props) {
    super(props);
    this.inputFieldRef = React.createRef();
  }

  @computed get height() {
    return this.expanded ? pixify(this.props.expandedHeight) : pixify(this.props.height);
  }

  @computed get submitOnEnter() {
    if (!this.props.submitOnEnter) {
      return this.props.inputType === 'text' ? true : false;
    }
    return this.props.submitOnEnter;
  }

  componentDidMount() {
    if (this.props.focusOnMount || this.props.focus) {
      this.focusInputDelayed();
    }
    this.updateExpandedForContent();
  }

  componentDidUpdate(prevProps) {
    if (this.props.caretPosition && this.props.caretPosition !== prevProps.caretPosition) {
      this.setCaretPosition();
    } else if ((this.props.focusOnMount && !prevProps.focusOnMount) || (!prevProps.focus && this.props.focus)) {
      this.focusInputDelayed();
    }
    if (this.props.value !== prevProps.value || this.props.height !== prevProps.height || this.props.expandedHeight !== prevProps.expandedHeight) {
      this.updateExpandedForContent();
    }
  }
  onKeyUp = (e) => {
    if (e.keyCode === 27) {
      this.inputFieldRef.current.blur();
      return;
    }

    if (this.props.rejectBlank && isBlank(this.inputFieldRef.current.value)) {
      return;
    }

    if (e.keyCode === 13 && (this.submitOnEnter || e.ctrlKey || e.altKey)) {
      if (this.props.onSubmit) {
        this.props.onSubmit(this.inputFieldRef.current.value);
      }
      if (this.props.contractOnSubmit) {
        this.expanded = false;
      }
      if (this.props.clearOnEnter) {
        this.inputFieldRef.current.value = '';
        if (this.props.onChange) {
          this.props.onChange('');
        }
      }
    }
  }
  hasNumber = (value) => {
    return isPresent(value) && value.match(/\d/);
  }

  setCaretPosition() {
    const input = this.inputFieldRef.current;
    if (input.setSelectionRange) {
      this.focusInput();
      input.setSelectionRange(this.props.caretPosition, this.props.caretPosition);
    } else if (input.createTextRange) {
      const range = input.createTextRange();
      range.collapse(true);
      range.moveEnd('character', this.props.caretPosition);
      range.moveStart('character', this.props.caretPosition);
      range.select();
    }
  }
  onChange = () => {
    if (this.props.blurOnNumber && this.hasNumber(this.inputFieldRef.current.value)) {
      this.inputFieldRef.current.blur();
    } else if (!this.props.readOnly && this.props.onChange) {
      this.props.onChange(this.inputFieldRef.current.value);
      this.updateExpandedForContent();
    }
  }
  onFocus = () => {
    if (this.props.expandOnOverflow) {
      this.updateExpandedForContent();
    } else {
      this.expanded = true;
    }
    if (this.props.onFocus) {
      this.props.onFocus();
    }
  }
  updateExpandedForContent = () => {
    if (!this.props.expandOnOverflow || this.props.inputType !== 'textarea' || !this.inputFieldRef.current || !this.props.height || !this.props.expandedHeight) {
      return;
    }
    const input = this.inputFieldRef.current;
    const originalHeight = input.style.height;
    input.style.height = pixify(this.props.height);
    if (input.scrollHeight > input.clientHeight + 1) {
      this.expanded = true;
    }
    input.style.height = originalHeight;
  }
  focusInputDelayed = () => {
    // without this there is a problem with the card animation
    setTimeout(this.focusInput, 100);
  }
  focusInput = () => {
    if (this.inputFieldRef.current) {
      this.inputFieldRef.current.focus();
    }
  }
  setInputRef = (el) => {
    this.inputFieldRef.current = el;
    if (typeof this.props.inputRef === 'function') {
      this.props.inputRef(el);
    } else if (this.props.inputRef) {
      this.props.inputRef.current = el;
    }
  }

  render() {
    if (this.props.inputType === 'textarea') {
      return (
        <textarea
          className={this.props.className}
          ref={this.setInputRef}
          onKeyUp={this.onKeyUp}
          onKeyDown={this.props.onKeyDown}
          onSelect={this.props.onSelect}
          onScroll={this.props.onScroll}
          placeholder={this.props.placeholder}
          onChange={this.onChange}
          onFocus={this.onFocus}
          value={this.props.value || ''}
          onBlur={this.props.onBlur}
          style={Object.assign({}, this.props.style, {height: this.height})}
          disabled={this.props.disabled}
          id={this.props.id}
          maxLength={this.props.maxLength}
        />
      );
    }
    return (
      <input
        className={this.props.className}
        type={this.props.type || 'text'}
        ref={this.setInputRef}
        spellCheck='false'
        step={this.props.step}
        max={this.props.max}
        min={this.props.min}
        maxLength={this.props.maxLength}
        onKeyUp={this.onKeyUp}
        onKeyDown={this.props.onKeyDown}
        placeholder={this.props.placeholder}
        onChange={this.onChange}
        onFocus={this.onFocus}
        value={isPresent(this.props.value) ? this.props.value : this.temporaryValue}
        onBlur={this.props.onBlur}
        style={this.props.style}
        disabled={this.props.disabled}
        readOnly={this.props.readOnly}
        tabIndex={this.props.tabIndex}
        id={this.props.id}
        autoComplete={this.props.showAutoComplete ? 'on' : 'off'}
      />
    );
  }
}

export default InPlaceTextInput;
